import React, { useEffect, useRef, useState } from 'react';
import useToast from '../../../hooks/useToast';
import { useAppDispatch } from '../../../redux/hooks';
import { TripCard } from '../../../redux/trip/trip.types';
import { handleNameKeyDown, handleNameSubmit } from '../../../utils/CardDetails';
import './InlineRename.scss';

interface InlineRenameProps {
  card: TripCard;
  tripId: string;
  isActive: boolean;
  isLarge?: boolean;
  isModal?: boolean;
  onComplete: () => void;
}

function InlineRename({
  card,
  tripId,
  isActive,
  isLarge = false,
  isModal = false,
  onComplete,
}: InlineRenameProps) {
  const dispatch = useAppDispatch();
  const { showSnackbarMessage } = useToast();
  const inputRef = useRef<HTMLHeadingElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Effect to trigger edit mode when isActive changes
  useEffect(() => {
    if (isActive && !isEditing && inputRef.current) {
      // Directly make the element editable instead of using the util function
      const element = inputRef.current;
      element.setAttribute('contentEditable', 'true');
      setIsEditing(true);

      // Focus and select all text
      element.focus();

      // Select all text in the editable element
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(element);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isActive, isEditing]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLHeadingElement>) => {
    event.stopPropagation(); // Prevent dnd-kit listeners on parent from intercepting
    if (event.key === 'Escape') {
      event.preventDefault();
      if (inputRef.current) {
        inputRef.current.innerText = card.customName ?? card.name ?? '';
        inputRef.current.blur();
      }
      setIsEditing(false);
      onComplete();
      return;
    }

    handleNameKeyDown(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLHeadingElement>) => {
    if (isEditing) {
      handleNameSubmit(event, card, tripId, dispatch, showSnackbarMessage);
      setIsEditing(false);
      onComplete();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLHeadingElement>) => {
    // Prevent the card click event from firing when clicking on the rename input
    event.stopPropagation();
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLHeadingElement>) => {
    // Also prevent mousedown events which might trigger the modal
    event.stopPropagation();
  };

  const containerClassName = [
    'inline-rename-container',
    isActive ? ' active' : '',
    isLarge && isModal ? ' large-modal' : isLarge ? ' large' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputClassName = ['inline-rename-input', isModal ? ' modal' : ''].filter(Boolean).join(' ');

  return (
    <div className={containerClassName}>
      <h3
        className={inputClassName}
        ref={inputRef}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        suppressContentEditableWarning={true}
      >
        {card.customName ?? card.name}
      </h3>
    </div>
  );
}

InlineRename.displayName = 'InlineRename';
export default InlineRename;
